import { useEffect, useState } from 'react';
import { fetchSceneRow } from '@/modules/databaseService/scenes';
import { getPublishedDevices } from '@/components/publish-flow/deviceAvailability';
import type { PublishDevice } from '@/components/publish-flow/types';

// Reads the platforms a scene was published/submitted to from its
// scene_instance_payload. `devices` is null when the scene has no publish-device
// array (drafts), so callers can leave those ungated. `loading` stays true until
// the fetch resolves so callers can avoid flashing every platform as available.
export function usePublishedDevices(
  sceneInstanceId: string | null | undefined,
): { devices: PublishDevice[] | null; loading: boolean } {
  const [devices, setDevices] = useState<PublishDevice[] | null>(null);
  const [loading, setLoading] = useState<boolean>(!!sceneInstanceId);

  // A new scene drops the old one's devices and loads until its own fetch resolves
  const [prevSceneInstanceId, setPrevSceneInstanceId] = useState(sceneInstanceId);
  if (prevSceneInstanceId !== sceneInstanceId) {
    setPrevSceneInstanceId(sceneInstanceId);
    setDevices(null);
    setLoading(!!sceneInstanceId);
  }

  useEffect(() => {
    if (!sceneInstanceId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await fetchSceneRow(sceneInstanceId);
        if (!cancelled) setDevices(getPublishedDevices(data?.scene_instance_payload));
      } catch (err) {
        console.error('usePublishedDevices', err);
        if (!cancelled) setDevices(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sceneInstanceId]);

  return { devices, loading };
}
